import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { useState, useEffect, Suspense } from 'react';
import { AuthProvider, useAuth } from './components/AuthProvider';
import LoginPage from './components/LoginPage';
import OktaCallback from './components/OktaCallback';
import OktaLogin from './components/OktaLogin';
import MicrosoftCallback from './components/MicrosoftCallback';
import Navigation from './components/Navigation';
import AdminNavigation from './components/AdminNavigation';
import ChatWidget from './components/ChatWidget';
import SearchBar from './components/SearchBar';
import MobileFooterNav from './components/MobileFooterNav';
import ChangeLogToast from './components/ChangeLogToast';
import StaleBuildReloader from './components/StaleBuildReloader';
import { lazyRoute } from './services/staleBuildReload';
import { BranchOverrideProvider, useBranchOverride } from './components/BranchOverrideContext';
import { AgencyProvider } from './components/AgencyProvider';
import { UserRoleProvider, useUserRole } from './components/UserRoleProvider';
import { ChatSessionProvider } from './components/chat/ChatSessionProvider';
import { PushNotificationProvider } from './components/PushNotificationProvider';
import { isChatSimulated, simulatedChatFactory, installChatSimulator } from './components/chat/simulatedBackend';
import { AppRoutes, usePageTitle } from './routes';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import './App.css';
// The page chrome every routed page is laid out in (.page, .page-content, .page-header). It belongs to
// the shell: imported from the pages instead, it rode in whichever lazy chunk happened to load first,
// so a page that did not import it for itself was styled or unstyled depending on where the agent had
// been beforehand. The pages keep their own imports; this one guarantees the rules are there.
import './pages/PageStyles.css';

// Lazy-load standalone /chat-popup view
const ChatPopup = lazyRoute(() => import('./pages/ChatPopup'));

function AppContent() {
  usePageTitle();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const { overrideUuid, overrideEmail, setOverride } = useBranchOverride();
  const { isAdmin } = useUserRole();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(() => localStorage.getItem('navCollapsed') === 'true');
  // The top bar's slot, beside the settings gear, that ChatWidget renders its launch menu into.
  const [topBarActions, setTopBarActions] = useState<HTMLDivElement | null>(null);
  const isNewGradPage = location.pathname === '/new-grad-roadmap';
  // A route that sizes itself to the viewport instead of scrolling within it. The policy review screen
  // fits a PDF pane, a field pane and a fixed action bar into whatever is left, which it can only do if
  // the site header and footer are out of the way and the space left over is a definite height.
  const isFullBleedPage = /^\/self-service\/policy-uploads\/[^/]+\/review\/[^/]+$/.test(location.pathname);
  // The assistant is a full-page chat: its transcript scrolls inside the page and its composer is
  // pinned to the bottom of the window, at every width. Unlike full-bleed, that holds on phones too —
  // a chat has no second pane to stack.
  const isChatPage = location.pathname === '/assistant';
  // The assistant keeps the logo header, for continuity with every other page, but not the footer: its
  // chat fills the rest of the viewport and the composer sits at the bottom of it.
  const hideHeader = isFullBleedPage;
  const hideFooter = isFullBleedPage || isChatPage;
  // The admin tools are their own app, with their own left nav, and only admins are ever in it: a
  // non-admin on an /admin page they hold a grant for gets the main nav.
  const isAdminArea = isAdmin && (location.pathname === '/admin' || location.pathname.startsWith('/admin/'));

  const toggleNavCollapse = () => {
    setNavCollapsed(prev => {
      localStorage.setItem('navCollapsed', String(!prev));
      return !prev;
    });
  };

  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);
  const closeSidebar = () => setIsSidebarOpen(false);

  // Lock body scroll when sidebar is open on mobile
  useEffect(() => {
    document.body.style.overflow = isSidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isSidebarOpen]);

  // Okta IdP-initiated SSO relay. Handled before the auth gate so it fires
  // regardless of app auth state — it immediately starts an SP-initiated PKCE
  // login so an access token is seated in tokenManager (see OktaLogin).
  if (location.pathname === '/okta/login') {
    return <OktaLogin />;
  }

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#0a202e',
        color: '#20cbd4',
      }}>
        <div>
          <h2>Loading...</h2>
          <p>Checking authentication status...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login/callback" element={<OktaCallback />} />
        <Route path="/microsoft/callback" element={<MicrosoftCallback />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <div className={`app ${navCollapsed ? 'nav-collapsed' : ''}`}>
      {isSidebarOpen && <div className="backdrop" onClick={closeSidebar} />}

      <header className="mobile-header">
        <button
          className="mobile-hamburger"
          onClick={toggleSidebar}
          aria-label="Toggle navigation menu"
        >
          <div className={`mobile-hamburger-icon ${isSidebarOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>
      </header>

      {isAdminArea ? (
        <AdminNavigation
          isOpen={isSidebarOpen}
          onClose={closeSidebar}
          onMenuToggle={toggleSidebar}
          isSidebarOpen={isSidebarOpen}
        />
      ) : (
        <Navigation
          isOpen={isSidebarOpen}
          onClose={closeSidebar}
          onMenuToggle={toggleSidebar}
          isSidebarOpen={isSidebarOpen}
        />
      )}
      <button
        className="nav-collapse-toggle"
        onClick={toggleNavCollapse}
        title={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
      >
        {navCollapsed ? <ChevronRightIcon fontSize="small" sx={{ verticalAlign: 'middle' }} /> : <ChevronLeftIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />}
      </button>

      <div className={`top-bar${isChatPage ? ' top-bar-standalone' : ''}`}>
        <SearchBar actionsRef={setTopBarActions} />
      </div>

      {(overrideUuid || overrideEmail) && (
        <div className="branch-override-bar">
          Signed-in as: {overrideEmail || 'another user'}
          <button onClick={() => {
            sessionStorage.removeItem('admin-login-recorded');
            setOverride(null, null);
            window.location.reload();
          }}>
            Reset to my account
          </button>
        </div>
      )}

      <main className={isFullBleedPage ? 'full-bleed' : isChatPage ? 'fill-viewport' : undefined}>
        {!isNewGradPage && !hideHeader && (
          <div className="main-header">
            <div className="main-header-content">
              <Link to="/">
                <img
                  src="/assets/we_insure_agencyos_horizontal.png"
                  alt="We Insure AgencyOS"
                  className="main-header-logo"
                />
              </Link>
            </div>
          </div>
        )}

        <AppRoutes />

        {!hideFooter && (
        <footer className="footer">
          <div className="footer-content">
            <div className="footer-logo">
              <img
                src="/assets/weinsuretech_horizontal.png"
                alt="We Insure Tech"
                className="footer-logo-img"
              />
            </div>
            <div className="footer-links">
              <a href="/status">System Status</a>
            </div>
            <p>&copy; 2026 We Insure Group. All rights reserved.</p>
          </div>
        </footer>
        )}
      </main>

      <MobileFooterNav onMenuToggle={toggleSidebar} isSidebarOpen={isSidebarOpen} />
      <ChatWidget actionsContainer={topBarActions} />
      <ChangeLogToast />
    </div>
  );
}

// Renders the popped-out chat window in place of the app when its route is
// active, so it is not wrapped in ChatSessionProvider.
//
// The popped-out chat is only a view of the session in the tab that opened it.
// Giving the window a provider of its own creates a second, empty transcript that
// answers the relay's popup-ready handshake and overwrites the real history with
// nothing.
const ChatWindows: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();

  if (pathname === '/chat-popup') {
    return (
      <Suspense fallback={<div />}>
        <ChatPopup />
      </Suspense>
    );
  }

  return <>{children}</>;
};

// Undefined in every real build, so the provider falls back to the ChatJS
// session. Resolved once at module load: swapping the session under a live chat
// is not a thing the app should be able to do.
const chatSessionFactory = isChatSimulated() ? simulatedChatFactory : undefined;
if (chatSessionFactory) installChatSimulator();

function App() {
  return (
    <Router>
      <StaleBuildReloader />
      <AuthProvider>
        {/* Acquires a token at launch for devices that have already opted in; it never prompts,
            so the permission request stays with the explicit opt-in in PushNotificationSettings. */}
        <PushNotificationProvider autoRegister={true}>
          <BranchOverrideProvider>
            <UserRoleProvider>
              <AgencyProvider>
                <ChatWindows>
                  {/* Above AppContent so a live chat survives route changes — the
                      widget is hidden on some routes, and while the session lived
                      inside it, navigating there dropped the conversation. */}
                  <ChatSessionProvider sessionFactory={chatSessionFactory}>
                    <AppContent />
                  </ChatSessionProvider>
                </ChatWindows>
              </AgencyProvider>
            </UserRoleProvider>
          </BranchOverrideProvider>
        </PushNotificationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
